import { Component } from '@theme/component';
import { RANCH_FILTERS_CHANGE, readRanchFilters } from '@theme/state-collection-filters';

/**
 * @typedef {import('./state-collection-filters.js').RanchFilters} RanchFilters
 */

/**
 * @typedef {object} Strings
 * @property {string[]} words - Number words, index = number (zero … twenty).
 * @property {{ one: string, other: string }} ranch - Singular / plural "ranch".
 * @property {{ one: string, other: string }} share - Singular / plural "share".
 */

/**
 * Ranch results (FindARancherDemo ResultsBar + RanchGrid): applies the
 * toolbar's filters to the server-rendered cards the way the demo's
 * `filteredRanches` memo does, then refreshes the heading counts and the
 * empty state. Filters come from the URL on load and from
 * `ranch-filters:change` afterwards.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [items] - Card wrappers carrying data-offers / data-delivery / data-ship-days / data-share-count.
 * @property {HTMLElement} grid - The card grid.
 * @property {HTMLElement} empty - The "no ranches match" message.
 * @property {HTMLElement} ranchCount - Heading slot for the ranch count word.
 * @property {HTMLElement} ranchLabel - Heading slot for "ranch" / "ranches".
 * @property {HTMLElement} shareCount - Heading slot for the share count word.
 * @property {HTMLElement} shareLabel - Heading slot for "share" / "shares".
 * @property {HTMLScriptElement} strings - JSON strings from Liquid.
 *
 * @extends {Component<Refs>}
 */
class StateCollectionRanchesComponent extends Component {
  requiredRefs = ['grid', 'empty', 'ranchCount', 'ranchLabel', 'shareCount', 'shareLabel', 'strings'];

  /** @type {Strings | undefined} */
  #strings;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#strings = JSON.parse(this.refs.strings.textContent ?? '{}');

    document.addEventListener(RANCH_FILTERS_CHANGE, this.#applyFiltersOnChange, {
      signal: this.#abortController.signal,
    });

    this.#applyFilters(readRanchFilters(window.location));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @param {Event} event
   */
  #applyFiltersOnChange = (event) => {
    const filters = /** @type {{ detail?: { filters?: RanchFilters } }} */ (event).detail?.filters;
    if (!filters) return;
    this.#applyFilters(filters);
  };

  /**
   * @param {RanchFilters} filters
   */
  #applyFilters(filters) {
    let ranchCount = 0;
    let shareCount = 0;

    for (const item of this.refs.items ?? []) {
      const matches = this.#matches(item, filters);
      item.classList.toggle('hidden', !matches);

      if (!matches) continue;
      ranchCount += 1;
      shareCount += Number(item.dataset.shareCount ?? 0);
    }

    this.refs.grid.classList.toggle('hidden', ranchCount === 0);
    this.refs.empty.classList.toggle('hidden', ranchCount > 0);
    this.#renderHeading(ranchCount, shareCount);
  }

  /**
   * Demo rules: a ranch passes when it offers the chosen share, offers the
   * chosen shipping method, and ships on at least one chosen day.
   *
   * @param {HTMLElement} item
   * @param {RanchFilters} filters
   * @returns {boolean}
   */
  #matches(item, { share, delivery, ships }) {
    const offers = (item.dataset.offers ?? '').split(' ');
    const deliveries = (item.dataset.delivery ?? '').split(' ');
    const shipDays = (item.dataset.shipDays ?? '').split(' ');

    if (share && !offers.includes(share)) return false;
    if (delivery && !deliveries.includes(delivery)) return false;
    if (ships.length && !ships.some((day) => shipDays.includes(day))) return false;
    return true;
  }

  /**
   * @param {number} ranchCount
   * @param {number} shareCount
   */
  #renderHeading(ranchCount, shareCount) {
    const strings = this.#strings;
    if (!strings) return;

    const ranchWord = this.#toWord(ranchCount);
    this.refs.ranchCount.textContent = ranchWord.charAt(0).toUpperCase() + ranchWord.slice(1);
    this.refs.ranchLabel.textContent = ranchCount === 1 ? strings.ranch.one : strings.ranch.other;
    this.refs.shareCount.textContent = this.#toWord(shareCount);
    this.refs.shareLabel.textContent = shareCount === 1 ? strings.share.one : strings.share.other;
  }

  /**
   * @param {number} count
   * @returns {string}
   */
  #toWord(count) {
    return this.#strings?.words[count] ?? String(count);
  }
}

if (!customElements.get('state-collection-ranches-component')) {
  customElements.define('state-collection-ranches-component', StateCollectionRanchesComponent);
}
